import React from 'react'
import { pipe } from 'fp-ts/lib/function'
import { Throwable, ZIO } from '@mxt/zio'
import { useLoading } from '@mxt/zio-react'

import {
  CheckboxTableColum,
  Columns,
  Container,
  ContractMapping,
  ErrorHandling,
  GeneralInfoService,
  HCR9BenefitInformationResponse,
  Panel,
  ProductExtInfo,
  ProductInfo,
  RowSelectedProps,
  Table,
  TableField,
  useTranslator,
  HCR9BenefitInformationModal,
  PulseOpsFormat
} from '@pulseops/common'

import i18n from 'i18next'
import { StyleSheet, Text } from 'react-native'
import { TouchableOpacity } from 'react-native-gesture-handler'
// import { HCR9BenefitInformationModal } from './HCR9BenefitInformationModal'
import * as O from 'fp-ts/lib/Option'
import moment from 'moment'

export interface ProductInfoData {
  productInfo: ProductExtInfo | null
  mainLAD: TableField.DataSources
}

export const ProductScreen = (props: {
  id: string
  data: ProductInfoData | null
  isSelectedTab: boolean
  setData: (val: ProductInfoData | null) => void
}) => {
  const { t } = useTranslator('TaskManagement')
  const { t: tPolicy } = useTranslator('TaskManagement')
  const ct = useTranslator('contract').t
  const commont = useTranslator('common').t
  const [loading, bindLoading] = useLoading()
  const [isOpenHCR9BenefitModal, setIsOpenOpenHCR9BenefitModal] = React.useState(false)
  const [HCR9BenefitInformation, setHCR9BenefitInformation] = React.useState<HCR9BenefitInformationResponse[]>([])

  React.useEffect(() => {
    return () => {
      setIsOpenOpenHCR9BenefitModal(false)
      setHCR9BenefitInformation([])
    }
  }, [props.isSelectedTab])

  const getData = (): ZIO<unknown, Throwable, ProductInfoData | null> =>
    pipe(
      GeneralInfoService.getProductInfo(props.id, i18n.language),
      ZIO.foldM(
        (_) => ZIO.succeed(null),
        (productInfo) =>
          ZIO.effect(() => {
            return {
              productInfo,
              mainLAD: productInfo.owner
                ? productInfo.owner.extInfos.map((e, i) => ({
                  number: ++i,
                  componentName: e.componentName,
                  status: ContractMapping.get(ct)(e.status),
                  sum: e.sumAssured,
                  instalmentPremium: e.installmentPremium,
                  paidToDate: e.paidToDate,
                  premiumCessationDate: e.premiumCessationDate,
                  lifeNo: productInfo.owner?.life || ''
                }))
                : []
            }
          })
      )
    )

  const { productInfo, mainLAD } = pipe(
    props.data ? ZIO.succeed(props.data) : getData(),
    ZIO.tap((x) => {
      console.log(x)
      if (x !== null && props.data === null) props.setData(x)
      return ZIO.unit
    }),
    bindLoading,
    ErrorHandling.runDidMount()
  ) || {
    productInfo: null,
    mainLAD: []
  }

  const columns = [
    {
      key: '1',
      title: `${commont('No.')}`,
      name: 'number',
      flex: 1,
      styles: styles.columTextLeft
    },
    {
      key: '2',
      title: t('ComponentName'),
      name: 'componentName',
      flex: 3,
      styles: styles.columTextLeft,
      render: (selectedRow: RowSelectedProps) => {
        const productName = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const componentName = rowData["componentName"] as string
            return componentName
          }),
          O.getOrElse(() => '')
        )
        const isHCR9Product = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const componentName = rowData["componentName"] as string
            const componentCode = componentName.split('-')[0]
            return componentCode === 'HCR9'
          }),
          O.getOrElse(() => false)
        )
        const lifeNo = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const lifeNoInfo = rowData["lifeNo"] as string
            return lifeNoInfo
          }),
          O.getOrElse(() => '')
        )
        return isHCR9Product ? (
          <TouchableOpacity onPress={() => onOpenHCR9BenefitModal(lifeNo)}>
            <Text style={styles.underlineLink}>{productName}</Text>
          </TouchableOpacity>
        ) : (
          <Text>{productName}</Text>
        )
      }
    },
    {
      key: '3',
      title: commont('Status'),
      name: 'status',
      flex: 1,
      styles: styles.columTextLeft
    },
    {
      key: '4',
      title: `${t('SumAssured')} (${t('basic')})`,
      name: 'sum',
      styles: styles.columTextRight,
      flex: 2,
      render: (selectedRow: RowSelectedProps) => {
        const sumAssured = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const sumData = rowData["sum"] as string
            return sumData
          }),
          O.getOrElse(() => '')
        )
        const isHCR9Product = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const componentName = rowData["componentName"] as string
            const componentCode = componentName.split('-')[0]
            return componentCode === 'HCR9'
          }),
          O.getOrElse(() => false)
        )
        return isHCR9Product ? <></> : (
          <Text>{sumAssured}</Text>
        )
      }
    },
    {
      key: '5',
      title: `${t('InstalmentPremium')} (${t('basic')})`,
      name: 'instalmentPremium',
      styles: styles.columTextRight,
      flex: 2
    },
    {
      key: '6',
      title: `${t('PaidToDate')} (${t('basic')})`,
      name: 'paidToDate',
      flex: 2,
      styles: styles.columTextLeft
    },
    {
      key: '7',
      title: `${t('PremCessDate')} (${t('basic')})`,
      name: 'premiumCessationDate',
      flex: 2,
      styles: styles.columTextLeft,
      render: (selectedRow: RowSelectedProps) => {
        const PremCessDate = pipe(
          selectedRow.rowData,
          O.fromNullable,
          O.map((rowData) => {
            const premiumCessationDate = rowData["premiumCessationDate"] as string
            return premiumCessationDate
          }),
          O.getOrElse(() => '')
        )
        const isHCR9Product = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const componentName = rowData["componentName"] as string
            const componentCode = componentName.split('-')[0]
            return componentCode === 'HCR9'
          }),
          O.getOrElse(() => false)
        )
        const customedPremCessDate = isHCR9Product
          ? pipe(
            O.fromNullable(HCR9BenefitInformation.find((item) => item.benefitCode === 'IP')),
            O.map((item) => {
              const premiumCessDateMoment = moment(item.premiumCessDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
              const premiumCessDate = premiumCessDateMoment.isValid() ? premiumCessDateMoment.format('DD/MM/YYYY') : ''
              return premiumCessDate
            }),
            O.getOrElse(() => PremCessDate)
          )
          : PremCessDate
        return <Text>{customedPremCessDate}</Text>
      }
    }
  ]

  const lifeAssuredColumns: Columns[] = [
    {
      key: '1',
      title: `${commont('No.')}`,
      name: 'number',
      flex: 1,
      styles: styles.columTextLeft
    },
    {
      key: '2',
      title: t('ComponentName'),
      name: 'componentName',
      flex: 3,
      styles: styles.columTextLeft,
      render: (selectedRow: RowSelectedProps) => {
        const productName = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const componentName = rowData["componentName"] as string
            return componentName
          }),
          O.getOrElse(() => '')
        )
        const isHCR9Product = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const componentName = rowData["componentName"] as string
            const componentCode = componentName.split('-')[0]
            return componentCode === 'HCR9'
          }),
          O.getOrElse(() => false)
        )
        const lifeNo = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const lifeNoInfo = rowData["lifeNo"] as string
            return lifeNoInfo
          }),
          O.getOrElse(() => '')
        )
        return isHCR9Product ? (
          <TouchableOpacity onPress={() => onOpenHCR9BenefitModal(lifeNo)}>
            <Text style={styles.underlineLink}>{productName}</Text>
          </TouchableOpacity>
        ) : (
          <Text>{productName}</Text>
        )
      }
    },
    {
      key: '3',
      title: t('CoverageStatus'),
      name: 'status',
      flex: 1,
      styles: styles.columTextLeft
    },
    {
      key: '4',
      title: `${t('SumAssured')} (${t('rider')})`,
      name: 'sum',
      flex: 2,
      styles: styles.columTextRight,
      render: (selectedRow: RowSelectedProps) => {
        const sumAssured = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const sumData = rowData["sum"] as string
            return sumData
          }),
          O.getOrElse(() => '')
        )
        const isHCR9Product = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const componentName = rowData["componentName"] as string
            const componentCode = componentName.split('-')[0]
            return componentCode === 'HCR9'
          }),
          O.getOrElse(() => false)
        )
        return isHCR9Product ? <></> : (
          <Text>{sumAssured}</Text>
        )
      }
    },
    {
      key: '5',
      title: `${t('InstalmentPremium')} (${t('rider')})`,
      name: 'instalmentPremium',
      flex: 2,
      styles: styles.columTextRight
    },
    {
      key: '6',
      title: `${t('PaidToDate')} (${t('rider')})`,
      name: 'paidToDate',
      flex: 2,
      styles: styles.columTextLeft
    },
    {
      key: '7',
      title: `${t('PremCessDate')} (${t('rider')})`,
      name: 'premiumCessationDate',
      flex: 2,
      styles: styles.columTextLeft,
      render: (selectedRow: RowSelectedProps) => {
        const PremCessDate = pipe(
          selectedRow.rowData,
          O.fromNullable,
          O.map((rowData) => {
            const premiumCessationDate = rowData["premiumCessationDate"] as string
            return premiumCessationDate
          }),
          O.getOrElse(() => '')
        )
        const isHCR9Product = pipe(
          O.fromNullable(selectedRow.rowData),
          O.map((rowData) => {
            const componentName = rowData["componentName"] as string
            const componentCode = componentName.split('-')[0]
            return componentCode === 'HCR9'
          }),
          O.getOrElse(() => false)
        )
        const customedPremCessDate = isHCR9Product ? pipe(
          O.fromNullable(HCR9BenefitInformation.find((item) => item.benefitCode === 'IP')),
          O.map((item) => {
            const premiumCessDateMoment = moment(item.premiumCessDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
            const premiumCessDate = premiumCessDateMoment.isValid() ? premiumCessDateMoment.format('DD/MM/YYYY') : ''
            return premiumCessDate
          }),
          O.getOrElse(() => PremCessDate)
        ) : PremCessDate
        return <Text>{customedPremCessDate}</Text>
      }
    }
  ]

  const benefitDisplayedColumns: CheckboxTableColum[] = [
    {
      title: t('PRUFlexiHealthBenefit:BenefitName'),
      fieldName: 'benefitName',
      disabled: true,
    },
    {
      title: t('PRUFlexiHealthBenefit:SumAssuredOfEachBenefit'),
      fieldName: 'sumInsurance',
      disabled: true,
      rowRender: (selectedRow) => {
        const sumInsurance = selectedRow.selectItem["sumInsurance"] as number
        return <Text>{PulseOpsFormat.thousandSepartor(sumInsurance)}</Text>
      }
    },
    {
      title: t('PRUFlexiHealthBenefit:InstallmentPremium'),
      fieldName: 'installPremium',
      disabled: true,
      rowRender: (selectedRow) => {
        const installPremium = selectedRow.selectItem["installPremium"] as number
        return <Text>{PulseOpsFormat.thousandSepartor(installPremium)}</Text>
      }
    },
    {
      title: t('PRUFlexiHealthBenefit:PremCessDate'),
      fieldName: 'premiumCessDate',
      disabled: true,
      rowRender: (selectedRow) => {
        const premiumCessDate = selectedRow.selectItem["premiumCessDate"] as string
        const premiumCessDateMoment = moment(premiumCessDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
        const customedPremiumCessDate = premiumCessDateMoment.isValid() ? premiumCessDateMoment.format('DD/MM/YYYY') : ''
        return <Text>{customedPremiumCessDate}</Text>
      }
    }
  ]

  function getLaTableFields(la: ProductInfo) {
    return la.extInfos.map((l, i) => ({
      number: (++i).toString(),
      componentName: l.componentName,
      status: ContractMapping.get(ct)(l.status),
      sum: l.sumAssured,
      instalmentPremium: l.installmentPremium,
      paidToDate: l.paidToDate,
      premiumCessationDate: l.premiumCessationDate,
      lifeNo: la.life
    }))
  }

  const onOpenHCR9BenefitModal = (lifeNo: string) => {
    if (!!props.id) {
      pipe(
        GeneralInfoService.getBenefitInformationForHCR9(props.id),
        ZIO.fold(
          (error) => {
            setHCR9BenefitInformation([])
            setIsOpenOpenHCR9BenefitModal(true)
          },
          (response) => {
            const HCR9BenefitList = response.filter((item) => item.lifeNo === lifeNo && item.sumInsurance > 0 && item.installPremium > 0)
            setHCR9BenefitInformation(HCR9BenefitList)
            setIsOpenOpenHCR9BenefitModal(true)
          }
        ),
        ZIO.unsafeRun({})
      )
    }
  }

  const onCloseHCR9BenefitModal = () => {
    setIsOpenOpenHCR9BenefitModal(false)
  }

  return (
    <Container loading={loading} display={!!productInfo}>
      <Panel title={productInfo?.owner?.name ?? ''} allowToggle={true} titleOutline={tPolicy('MainLifeAssured')}>
        <Table
          columns={columns}
          dataSource={mainLAD}
          headerContainerStyle={{ alignItems: 'flex-start' }}
          headerTitleStyle={{ textAlign: 'left' }}
        />
      </Panel>
      {productInfo?.laClient.slice(0, 1).map((item, i) => (
        <Panel title={item?.name || '-'} titleOutline={commont('LifeAssured')} key={`ProductScreenMAIN-${i}`}>
          <Table columns={lifeAssuredColumns} dataSource={getLaTableFields(item)} />
        </Panel>
      ))}
      {productInfo?.laClient.slice(1).map((item, i) => (
        <Panel title={item?.name || '-'} key={`ProductScreenSUB-${i}`}>
          <Table columns={lifeAssuredColumns} dataSource={getLaTableFields(item)} />
        </Panel>
      ))}
      <HCR9BenefitInformationModal
        isOpen={isOpenHCR9BenefitModal}
        modalTitle={t('PRUFlexiHealthBenefit:PRUFlexiHealthBenefitDetails')}
        HCR9BenefitInformation={HCR9BenefitInformation}
        displayedColumns={benefitDisplayedColumns}
        handleClose={onCloseHCR9BenefitModal}
      />
    </Container>
  )
}

const styles = StyleSheet.create({
  columTextRight: {
    textAlign: 'right',
    paddingHorizontal: 15
  },
  columTextLeft: {
    textAlign: 'left',
    paddingHorizontal: 15
  },
  underlineLink: {
    color: 'blue',
    textDecorationLine: 'underline'
  }
})
